function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _type_of(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
}
import { ageRestrictions } from 'shared/constants';
import { getComplianceConfig } from 'shared/core/helpers/compliance';
import { normalizeCountryCode } from 'shared/helpers/geography';
// Match the order validator's oldest accepted customer age so persisted settings cannot create an impossible boundary.
var MAXIMUM_AGE_OVERRIDE = 120;
var validAgeOverride = function(value) {
    return Number.isInteger(value) && value > 0 && value <= MAXIMUM_AGE_OVERRIDE;
};
var getEffectiveMinimumAges = function(overrides, medDutchieMinimum, recDutchieMinimum) {
    var _overrides_medical;
    var medMinAge = Math.max((_overrides_medical = overrides.medical) !== null && _overrides_medical !== void 0 ? _overrides_medical : medDutchieMinimum, medDutchieMinimum);
    var _overrides_rec;
    var recMinAge = Math.max((_overrides_rec = overrides.rec) !== null && _overrides_rec !== void 0 ? _overrides_rec : recDutchieMinimum, recDutchieMinimum, medMinAge);
    return {
        medMinAge: medMinAge,
        recMinAge: recMinAge
    };
};
var sanitizeAgeOverrides = function(overrides) {
    var values = overrides !== null && overrides !== void 0 ? overrides : {};
    return _object_spread({}, validAgeOverride(values.medical) ? {
        medical: values.medical
    } : {}, validAgeOverride(values.rec) ? {
        rec: values.rec
    } : {});
};
var getAgeOverrideValuesValidationError = function(overrides, country, region) {
    if (overrides.medical != null && !validAgeOverride(overrides.medical) || overrides.rec != null && !validAgeOverride(overrides.rec)) {
        return 'Minimum-age overrides must be whole numbers from 1 through 120.';
    }
    var _getAgeRestriction = getAgeRestriction({
        country: country,
        region: region
    }), medMinAge = _getAgeRestriction.medMinAge, recMinAge = _getAgeRestriction.recMinAge;
    if (overrides.medical != null && overrides.medical < medMinAge) {
        return "Medical minimum age cannot be below the Dutchie default of ".concat(medMinAge, ".");
    }
    if (overrides.rec != null && overrides.rec < recMinAge) {
        return "Recreational minimum age cannot be below the Dutchie default of ".concat(recMinAge, ".");
    }
    var _overrides_medical, _overrides_rec;
    if (((_overrides_medical = overrides.medical) !== null && _overrides_medical !== void 0 ? _overrides_medical : medMinAge) > ((_overrides_rec = overrides.rec) !== null && _overrides_rec !== void 0 ? _overrides_rec : recMinAge)) {
        return 'Medical minimum age must be less than or equal to recreational minimum age.';
    }
    return null;
};
export function getMinimumAgeOverrideValidationError(overrides, location) {
    if (overrides == null) return null;
    if ((typeof overrides === "undefined" ? "undefined" : _type_of(overrides)) !== 'object' || Array.isArray(overrides)) {
        return 'Minimum-age overrides must be a JSON object.';
    }
    var country = normalizeCountryCode(location === null || location === void 0 ? void 0 : location.country) || 'US';
    var region = location === null || location === void 0 ? void 0 : location.state;
    if (normalizeCountryCode(overrides.country) !== country || overrides.region !== region) {
        return 'Minimum-age overrides must match the location country and region.';
    }
    if (overrides.medical == null && overrides.rec == null) {
        return 'Select at least one minimum-age override or clear the setting.';
    }
    return getAgeOverrideValuesValidationError(overrides, country, region);
}
// Temporary compatibility validation while older Admin clients still write nested Purchase Limits ages.
export function getLegacyAgeOverrideValidationError(configuration, location) {
    if (!configuration || configuration.ageOverrides == null) return null;
    if (_type_of(configuration.ageOverrides) !== 'object' || Array.isArray(configuration.ageOverrides)) {
        return 'Minimum-age overrides must be a JSON object.';
    }
    return getAgeOverrideValuesValidationError(configuration.ageOverrides, normalizeCountryCode(location === null || location === void 0 ? void 0 : location.country) || 'US', location === null || location === void 0 ? void 0 : location.state);
}
var getCustomerAgeOverrides = function(geoInfo, dispensary, minimumAgeOverridesFeatureEnabled, configurablePurchaseLimitsFeatureEnabled) {
    var _dispensary_location, _dispensary_location1, _dispensary_storeSettings;
    var locationCountry = normalizeCountryCode(dispensary === null || dispensary === void 0 ? void 0 : (_dispensary_location = dispensary.location) === null || _dispensary_location === void 0 ? void 0 : _dispensary_location.country) || 'US';
    var requestedCountry = normalizeCountryCode(geoInfo === null || geoInfo === void 0 ? void 0 : geoInfo.country);
    var locationRegion = dispensary === null || dispensary === void 0 ? void 0 : (_dispensary_location1 = dispensary.location) === null || _dispensary_location1 === void 0 ? void 0 : _dispensary_location1.state;
    var requestedRegion = geoInfo === null || geoInfo === void 0 ? void 0 : geoInfo.region;
    if (minimumAgeOverridesFeatureEnabled) {
        var _dispensary_storeSettings1;
        var overrides = dispensary === null || dispensary === void 0 ? void 0 : (_dispensary_storeSettings1 = dispensary.storeSettings) === null || _dispensary_storeSettings1 === void 0 ? void 0 : _dispensary_storeSettings1.minimumAgeOverrides;
        if (overrides && normalizeCountryCode(overrides.country) === locationCountry && overrides.region === locationRegion && requestedCountry === locationCountry && requestedRegion === locationRegion) {
            return sanitizeAgeOverrides(overrides);
        }
    }
    if (!configurablePurchaseLimitsFeatureEnabled || !(dispensary === null || dispensary === void 0 ? void 0 : (_dispensary_storeSettings = dispensary.storeSettings) === null || _dispensary_storeSettings === void 0 ? void 0 : _dispensary_storeSettings.configurablePurchaseLimitsEnabled)) {
        return {};
    }
    var legacyConfiguration = dispensary.storeSettings.customPurchaseLimits;
    if (!legacyConfiguration || legacyConfiguration.state !== locationRegion || requestedRegion !== locationRegion) {
        return {};
    }
    return sanitizeAgeOverrides(legacyConfiguration.ageOverrides);
};
/**
 * Returns age restriction configuration for a given geo location.
 * @param {Object} geoInfo - Object containing country and region
 * @param {Object} options - Optional dispensary and rollout context
 * @returns {Object} Object with medMinAge, recMinAge, requiresBirthdayEntry, and forceSessionStorage
 */ export function getAgeRestriction(geoInfo) {
    var options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    var _ageRestrictions_country;
    var _ref = geoInfo || {}, country = _ref.country, region = _ref.region;
    var dispensary = options.dispensary, _options_minimumAgeOverridesFeatureEnabled = options.minimumAgeOverridesFeatureEnabled, minimumAgeOverridesFeatureEnabled = _options_minimumAgeOverridesFeatureEnabled === void 0 ? false : _options_minimumAgeOverridesFeatureEnabled, _options_configurablePurchaseLimitsFeatureEnabled = options.configurablePurchaseLimitsFeatureEnabled, configurablePurchaseLimitsFeatureEnabled = _options_configurablePurchaseLimitsFeatureEnabled === void 0 ? false : _options_configurablePurchaseLimitsFeatureEnabled;
    var ageOverrides = getCustomerAgeOverrides(geoInfo, dispensary, minimumAgeOverridesFeatureEnabled, configurablePurchaseLimitsFeatureEnabled);
    // For Canada, use the existing age restrictions constants
    // Canada already has birthday entry via the territory picker modal
    if (country === 'CA') {
        var provinceAge = ageRestrictions.CA[region] || ageRestrictions.CA.default;
        return _object_spread_props(_object_spread({}, getEffectiveMinimumAges(ageOverrides, provinceAge, provinceAge)), {
            requiresBirthdayEntry: true,
            forceSessionStorage: false
        });
    }
    // For US states, use compliance config which has detailed state-specific rules
    if (country === 'US') {
        var medMinAge = getComplianceConfig(region, 'medMinAge');
        var recMinAge = getComplianceConfig(region, 'recMinAge');
        var requiresBirthdayEntry = getComplianceConfig(region, 'requiresBirthdayEntry') || false;
        var forceSessionStorage = getComplianceConfig(region, 'forceSessionStorage') || false;
        return _object_spread_props(_object_spread({}, getEffectiveMinimumAges(ageOverrides, medMinAge, recMinAge)), {
            requiresBirthdayEntry: requiresBirthdayEntry,
            forceSessionStorage: forceSessionStorage
        });
    }
    // For other countries, use default age restriction
    var defaultAge = ((_ageRestrictions_country = ageRestrictions[country]) === null || _ageRestrictions_country === void 0 ? void 0 : _ageRestrictions_country.default) || ageRestrictions.default;
    return _object_spread_props(_object_spread({}, getEffectiveMinimumAges(ageOverrides, defaultAge, defaultAge)), {
        requiresBirthdayEntry: false,
        forceSessionStorage: false
    });
}
